import { ref, computed, readonly, watch } from 'vue'
import { defu } from 'defu'
import NoOrdersOverlay from '@/Components/DataGrid/Overlays/NoOrdersOverlay.vue'
import { useShowOverlayWhenNoRowsVisible } from '@/Components/DataGrid/Composables/no-rows-overlay'
import { useCheckboxCounter } from '@/Components/DataGrid/Composables/checkbox-column'
import { useScreens } from '@/Composables/screens'

const defaultGridOptions = {
  // Define the overlay component to show when there are no rows.
  noRowsOverlayComponent: NoOrdersOverlay,
  // Prevent row selection when clicking on a row
  // When using the checkbox column to select multiple rows, users sometimes accidentally click slightly outside of the checkbox.
  // With row click selection on, this resets the multi-selection state and selects just the row that was clicked.
  suppressRowClickSelection: true,
  // AG Grid's animation is jank.
  animateRows: false
}

const { isNarrowScreen, isShortScreen } = useScreens()

const domLayout = computed(() => (isNarrowScreen.value || isShortScreen.value ? 'autoHeight' : 'normal'))

export const useGridApi = () => {
  const isServerSide = ref(false)
  const gridApi = ref()

  useShowOverlayWhenNoRowsVisible({ gridApi: readonly(gridApi) })
  useCheckboxCounter({ gridApi: gridApi })

  function createGridOptions(...options) {
    const gridOptions = defu(...options)

    isServerSide.value = gridOptions.rowModelType === 'serverSide'

    // Client side tables should always use the filtered only header checkbox selection.
    if (!isServerSide.value) {
      const checkCols = gridOptions.columnDefs.filter((col) => col.field === 'checkbox')
      if (checkCols.length > 0) {
        checkCols.forEach((col) => {
          col.headerCheckboxSelectionFilteredOnly = true
        })
      }
    }

    return computed(() => {
      // If the domLayout option is not set, the defualt is the dynamic
      // domLayout value based on the screen size.
      if (!('domLayout' in gridOptions)) {
        defaultGridOptions.domLayout = domLayout.value
      }
      return defu(gridOptions, defaultGridOptions)
    })
  }

  watch(
    gridApi,
    () => {
      // Add a warning to the console if the user is trying to get the selected nodes on a server side table.
      // We have custom selected rows behaviour for SSRM tables.
      if (isServerSide.value) {
        const getSelectedNodes = gridApi.value.getSelectedNodes.bind(gridApi.value)
        gridApi.value.getSelectedNodes = () => {
          console.warn('Salve: Use the useSelectedRowNodes composable to get the selected nodes on SSRM tables.')

          return getSelectedNodes()
        }
        // When using server side pagination, we want to deselect all rows when the page changes.
        gridApi.value.addEventListener('paginationChanged', () => {
          gridApi.value.deselectAll()
        })
      }
    },
    { once: true }
  )

  return {
    gridApi,
    createGridOptions
  }
}
